'use client';

import React from 'react';
import { LocaleLink as Link } from "@/components/shared/LocaleLink";
import { ArrowRight, CheckCircle2, TrendingDown, Share2, Package } from 'lucide-react';
import { CalculationResult } from './CalculatorForm';
import { formatPrice } from '@/lib/pricingData';
import { useTranslations } from '@/lib/i18n-client';

interface SavingsVisualizerProps {
  result: CalculationResult | null;
}

export default function SavingsVisualizer({ result }: SavingsVisualizerProps) {
  const { t } = useTranslations();
  if (!result) return null;

  const savingsPercent = Math.round((result.savings / result.carrierCost) * 100);

  const handleShare = () => {
    const text = t('savings.shareText', 'I just saved {savings} on my travel data to {destination} using eSIMSupra! Check your savings here:')
      .replace('{savings}', formatPrice(result.savings))
      .replace('{destination}', result.destination.name);
    const url = typeof window !== 'undefined' ? window.location.href : '';
    if (navigator.share) {
      navigator.share({ title: t('savings.shareTitle', 'Travel Data Savings'), text, url });
    } else {
      navigator.clipboard.writeText(`${text} ${url}`);
      alert('Copied to clipboard!');
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="glass p-12 rounded-[2.5rem] border border-primary/20 bg-gradient-to-br from-primary/5 via-background to-background relative overflow-hidden">
        {/* Glow Effect */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 blur-[100px] -mr-32 -mt-32" />
        
        <div className="relative z-10 text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/10 border border-green-500/20 text-green-500 text-xs font-black uppercase tracking-widest">
            <TrendingDown className="w-4 h-4" />
            <span>{t('savings.complete', 'Calculation Complete')}</span>
          </div>

          <h2 className="text-4xl sm:text-5xl font-black tracking-tight">
            {t('savings.youSave', 'You save')} <span className="text-primary">{formatPrice(result.savings)}</span>
          </h2>
          
          <p className="text-xl text-muted-foreground">
            {t('savings.reduction', "That's a {percent}% reduction in your travel expenses for {destination}.")
              .replace('{percent}', String(savingsPercent))
              .replace('{destination}', result.destination.name)}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8">
            <div className="p-6 rounded-3xl bg-background/50 border border-border/50 text-left space-y-2">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">{t('savings.carrierCost', 'Carrier Cost')}</span>
              <p className="text-2xl font-black line-through opacity-40">{formatPrice(result.carrierCost)}</p>
            </div>
            <div className="p-6 rounded-3xl bg-primary/10 border border-primary/20 text-left space-y-2 relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-4 opacity-20 group-hover:scale-110 transition-transform">
                  <Package className="w-12 h-12" />
               </div>
               <span className="text-xs font-bold text-primary uppercase tracking-widest">{t('savings.supraCost', 'eSIMSupra Cost')}</span>
               <p className="text-3xl font-black text-primary">{formatPrice(result.supraCost)}</p>
            </div>
          </div>
        </div>
      </div>

      {result.bestPlan && (
        <div className="glass p-8 rounded-[2.5rem] border border-border/50 flex flex-col md:flex-row items-center gap-8 justify-between group hover:border-primary/30 transition-all duration-500">
          <div className="space-y-2 text-center md:text-left">
            <div className="flex items-center gap-2 text-xs font-bold text-muted-foreground uppercase tracking-widest mb-2 justify-center md:justify-start">
               <CheckCircle2 className="w-4 h-4 text-green-500" />
               {t('savings.bestPlanLabel', 'Highest Value Plan Found')}
            </div>
            <h3 className="text-2xl font-black">{result.bestPlan.Name}</h3>
            <p className="text-muted-foreground">
               {t('savings.bestPlanDetail', '{gb}GB Data • {days} Days Validity • 5G Speed')
                 .replace('{gb}', String(result.bestPlan.GBs))
                 .replace('{days}', String(result.bestPlan.ValidityDays))}
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
            <button 
              onClick={handleShare}
              className="flex-1 px-8 py-4 rounded-2xl border border-border hover:bg-border/50 font-bold transition-all flex items-center justify-center gap-2"
            >
              <Share2 className="w-4 h-4" /> {t('savings.shareResults', 'Share Results')}
            </button>
            <Link 
              href={`/destinations/${result.bestPlan.Code.toLowerCase()}/`}
              className="flex-1 px-8 py-4 rounded-2xl bg-primary text-primary-foreground font-black transition-all flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-primary/20"
            >
              {t('savings.getPlan', 'Get This Plan')} <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      )}

      {/* Trust Quote */}
      <div className="text-center p-8 bg-muted/30 rounded-3xl border border-border/30 italic text-muted-foreground text-sm">
        &ldquo;{t('savings.trustQuote', 'Switching to an eSIM before you leave the airport is the single easiest way to avoid bill shock upon returning home.')}&rdquo;
      </div>
    </div>
  );
}
